import React, { FC } from 'react';
import { Pencil, Info, Search, Check, ShieldCheck } from 'lucide-react';
import {
  CellState,
  ReviewCell,
  ReviewContent,
  EditIconButton,
  StatusIconContainer,
  BankNameTooltip,
} from '../NewVerificationTab.styles';
import { ExpandableTableRow, DataPointType } from '../NewVerificationTab.data';
import { resolveReviewCellText, cellContentToString, isEmptyValue } from '../NewVerificationTab.utils';
import useBankNameBySwiftCode from '../hooks/useBankNameBySwiftCode';
import useBankNameByCountryCodeAndBankCode from '../hooks/useBankNameByCountryCodeAndBankCode';

interface ReviewCellContentProps {
  row: ExpandableTableRow;
  colKey: string;
  state: CellState;
  rowIndex: number;
  /** Show a shield next to the value when this legal-identifier's type + country is Semi-Strong. */
  showSemiStrongShield?: boolean;
  /** Country code presented in this table's Account Country row — keys the bank-code bank-name hover. */
  accountCountryCode?: string;
  canEdit: boolean;
  onEditClick: (
    colKey: 'manualProcess' | 'finalReview',
    rowIndex: number,
    currentValue: React.ReactNode,
    dataPointType: DataPointType,
  ) => void;
}

const ReviewIcon: FC<{ state: CellState }> = ({ state }) => {
  switch (state.reviewIcon) {
    case 'search':
      return (
        <StatusIconContainer>
          <Search className='w-4 h-4 text-[#f5a623]' />
        </StatusIconContainer>
      );
    case 'info':
      return (
        <StatusIconContainer>
          <Info className='w-4 h-4 text-[#ff8800]' />
        </StatusIconContainer>
      );
    case 'check':
      return (
        <StatusIconContainer>
          <Check className='w-4 h-4 text-green-600' />
        </StatusIconContainer>
      );
    case 'question':
      return (
        <StatusIconContainer>
          <span className='text-[#1c2024] text-sm font-bold leading-4'>?</span>
        </StatusIconContainer>
      );
    default:
      return null;
  }
};

export const ReviewCellContent: FC<ReviewCellContentProps> = ({
  row,
  colKey,
  state,
  rowIndex,
  showSemiStrongShield,
  accountCountryCode,
  canEdit,
  onEditClick,
}) => {
  const reviewText = resolveReviewCellText(row, colKey, state);

  // DB bank name for a saved SWIFT / bank-code value — shown as a native hover tooltip on the value.
  // SWIFT resolves by the code alone (the hook gates on a valid full SWIFT); bank code resolves by
  // the column's presented account country + the code. Both hooks debounce; undefined => no-op.
  const swiftValue = row.dataPointType === 'swiftCode' ? cellContentToString(reviewText) : undefined;
  const swiftBankNameLoadable = useBankNameBySwiftCode(swiftValue);
  const bankCodeValue =
    row.dataPointType === 'bankCode' && !isEmptyValue(cellContentToString(reviewText))
      ? cellContentToString(reviewText)
      : undefined;
  const bankCodeBankNameLoadable = useBankNameByCountryCodeAndBankCode(accountCountryCode, bankCodeValue);
  const dbBankName =
    swiftBankNameLoadable.resolve(
      (name) => name,
      () => null,
    ) ??
    bankCodeBankNameLoadable.resolve(
      (name) => name,
      () => null,
    );

  return (
    <ReviewCell>
      <ReviewIcon state={state} />
      <ReviewContent data-testid={`verification-value-${colKey}-${row.dataPointType}`}>
        {dbBankName ? (
          // Anchor the tooltip to a span sized to the text (inline-block), not the full-width
          // flex ReviewContent, so the popup centers over the value rather than the whole cell.
          <BankNameTooltip title={dbBankName} mouseEnterDelay={0.1} mouseLeaveDelay={0.15}>
            <span style={{ display: 'inline-block' }}>{reviewText}</span>
          </BankNameTooltip>
        ) : (
          reviewText
        )}
      </ReviewContent>
      {showSemiStrongShield && (
        <StatusIconContainer title='Semi-Strong LEI'>
          <ShieldCheck className='w-4 h-4 text-green-600' />
        </StatusIconContainer>
      )}
      {canEdit && (
        <EditIconButton
          data-testid={`verification-edit-${colKey}-${row.dataPointType}`}
          onClick={(): void =>
            onEditClick(
              colKey as 'manualProcess' | 'finalReview',
              rowIndex,
              row[colKey as keyof ExpandableTableRow] as React.ReactNode,
              row.dataPointType,
            )
          }
        >
          <Pencil className='w-4 h-4 text-gray-600' />
        </EditIconButton>
      )}
    </ReviewCell>
  );
};
